interface PageState {
  nextOffset: number;
  hasMore: boolean;
  loading: boolean;
}

// Shared shape/pattern-library catalog loader (design-3d.component.ts) - lazily paginates each
// collection's items, 50 at a time, tracking per-collection offset/hasMore/loading in a Map so
// re-opening an already-loading or fully-loaded collection is a no-op. Mirrors design.component.ts's
// getShapeCollections()/loadShapePage()/ensureShapesLoaded(), minus its background "prewarm"
// scheduler (a page-load performance optimization, not functionally required - this loads lazily on
// first open instead). Generic over the item/collection shape and the fetch function so the exact
// same loader serves both the Shape and Pattern libraries, which only differ in which
// DesignService method backs fetchPage.
export class PaginatedCollectionLoader<TItem, TCollection extends { id: number }> {
  collections: TCollection[] = [];
  items: TItem[] = [];
  private pages = new Map<number, PageState>();

  constructor(
    private readonly pageSize: number,
    private readonly fetchPage: (collectionId: number, offset: number, pageSize: number) => Promise<{ content: TItem[]; last: boolean }>,
  ) {}

  // Sets the collection list (already fetched/filtered by the caller - filtering on a
  // collection-type-specific field, e.g. shapes>0 vs patterns>0, isn't this loader's concern) and
  // eagerly starts loading every collection's first page, same as the old per-side methods did.
  loadCollections(collections: TCollection[]): Promise<void[]> {
    this.collections = collections;
    return this.ensureAllLoaded();
  }

  private loadPage(collectionId: number): Promise<void> {
    const state = this.pages.get(collectionId);
    if (state && (state.loading || !state.hasMore)) {
      return Promise.resolve();
    }
    const offset = state?.nextOffset ?? 0;
    this.pages.set(collectionId, { nextOffset: offset, hasMore: true, loading: true });
    return this.fetchPage(collectionId, offset, this.pageSize).then((page) => {
      page.content.forEach((item) => this.items.push(item));
      this.pages.set(collectionId, { nextOffset: offset + 1, hasMore: !page.last, loading: false });
    });
  }

  ensureLoaded(collectionId: number): Promise<void> {
    if (this.pages.has(collectionId)) {
      return Promise.resolve();
    }
    return this.loadPage(collectionId);
  }

  ensureAllLoaded(): Promise<void[]> {
    return Promise.all(this.collections.map((collection) => this.ensureLoaded(collection.id)));
  }

  // Loads the next page of whichever collection isn't fully loaded (or hasn't started) yet - the
  // infinite-scroll sentinel's (nearBottom) handler calls this, one page per trigger.
  loadMore(): void {
    const next = this.collections.find((collection) => {
      const state = this.pages.get(collection.id);
      return !state || (state.hasMore && !state.loading);
    });
    if (next) {
      void this.loadPage(next.id);
    }
  }

  getForCollection(collectionId: number): TItem[] {
    return this.items.filter((item) => (item as unknown as { collection: { id: number } }).collection.id === collectionId);
  }
}
